import React, { useState, useRef } from 'react';
import api from '@/api/axios';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { cn } from '@/components/ui/utils';

const { FiUploadCloud, FiFile, FiCheckCircle, FiAlertCircle, FiX, FiInfo, FiDownload, FiTerminal } = FiIcons;

const UploadUsers = () => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const downloadSampleCSV = () => {
    const headers = "language,mobile_no,outlet_name,achieved_slab,gift_option_1,gift_option_2,gift_option_3,pan_card\n";
    const rows = "English,9876543210,Shree Ram Enterprises,Platinum,Amazon Voucher,Gold Coin,T-Shirt,Pending\n";
    const rows2 = "Hindi,8765432109,Gupta Traders,Gold,Flipkart Card,Silver Coin,Cap,Not Required";
    
    const blob = new Blob([headers + rows + rows2], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'Rewards_Sample_Import.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.endsWith('.csv')) setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setResult(null);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    const interval = setInterval(() => setProgress(p => Math.min(p + 5, 95)), 200);

    try {
      const res = await api.post('upload_users.php', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      clearInterval(interval);
      setProgress(100);
      setResult({ success: true, ...res.data });
      setFile(null);
    } catch (error) {
      clearInterval(interval);
      setResult({ 
        success: false, 
        message: error.response?.data?.message || 'File upload failed. Please check your connection.',
        failed_details: error.response?.data?.failed_details || [] 
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex justify-between items-center px-2">
        <div>
          <h2 className="text-xl font-black text-surface-900 uppercase tracking-tight">Import Users</h2>
          <p className="text-xs text-surface-400 font-bold uppercase tracking-wider">Campaign Database Sync</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={downloadSampleCSV}
          className="rounded-xl border-primary-100 bg-primary-50 text-primary-700 hover:bg-primary-100 h-9"
        >
          <SafeIcon icon={FiDownload} className="w-3.5 h-3.5 mr-2" />
          <span className="text-[10px] font-black uppercase tracking-widest">Download Sample</span>
        </Button>
      </div>

      <Card className="border-none shadow-xl shadow-surface-200/50 rounded-[2.5rem] bg-white">
        <CardContent className="pt-6 space-y-5">
          <div 
            className={cn(
              "relative border-2 border-dashed rounded-[2rem] p-12 text-center cursor-pointer transition-all duration-300",
              isDragging ? "border-primary-500 bg-primary-50 scale-[1.01]" : "border-surface-200 hover:border-primary-400 hover:bg-primary-50/20",
              file && "border-primary-400 bg-primary-50/50"
            )}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {file ? (
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 rounded-[1.5rem] bg-primary-600 flex items-center justify-center shadow-lg shadow-primary-200">
                  <SafeIcon icon={FiFile} className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="font-bold text-surface-900">{file.name}</p>
                  <p className="text-xs text-surface-400 font-bold mt-1">{(file.size / 1024).toFixed(1)} KB • Validated</p>
                </div>
                <button className="text-[10px] font-black uppercase tracking-widest text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                  [ Remove File ]
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-4">
                <div className="w-20 h-20 rounded-[2rem] bg-surface-50 flex items-center justify-center border border-surface-100 group-hover:bg-primary-50 transition-colors">
                  <SafeIcon icon={FiUploadCloud} className="w-10 h-10 text-surface-300 group-hover:text-primary-500" />
                </div>
                <div>
                  <p className="font-black text-surface-900 text-lg">Drop CSV here</p>
                  <p className="text-sm text-surface-400 mt-1 font-medium">Rewards Server v1.1 (CSV Import)</p>
                </div>
              </div>
            )}
          </div>

          {isUploading && (
            <div className="space-y-3 px-2">
              <div className="flex justify-between items-end">
                <span className="text-[10px] font-black uppercase text-primary-600 tracking-[0.2em] animate-pulse">Processing Data...</span>
                <span className="text-lg font-black text-primary-700">{progress}%</span>
              </div>
              <div className="h-3 bg-surface-100 rounded-full overflow-hidden p-0.5 border border-surface-200">
                <div className="h-full bg-primary-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={handleUpload} disabled={!file || isUploading} className="h-12 px-10 rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg shadow-primary-200">
              {isUploading ? 'Executing...' : 'Start Upload'}
            </Button>
          </div>

          {result && (
            <div className={cn("rounded-[2rem] p-6 border-2", result.success ? "bg-emerald-50/50 border-emerald-100" : "bg-red-50/50 border-red-100")}>
              <div className="flex items-center space-x-3 mb-6">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shadow-sm", result.success ? "bg-emerald-500" : "bg-red-500")}>
                  <SafeIcon icon={result.success ? FiCheckCircle : FiAlertCircle} className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className={cn("font-black text-sm uppercase tracking-tight", result.success ? "text-emerald-900" : "text-red-900")}>
                    {result.success ? 'Import Completed' : 'Import Failed'}
                  </h4>
                  <p className={cn("text-[11px] font-bold uppercase opacity-60", result.success ? "text-emerald-700" : "text-red-700")}>
                    {result.success ? 'Database transaction finalized' : 'Errors found in CSV structure'}
                  </p>
                </div>
              </div>

              {result.success ? (
                <div className="grid grid-cols-3 gap-4">
                  {[
                    ['Uploaded', result.uploaded || 0, 'bg-white border-emerald-200 text-emerald-700'],
                    ['Duplicates', result.duplicates || 0, 'bg-white border-amber-200 text-amber-700'],
                    ['Failed', result.failed || 0, 'bg-white border-red-200 text-red-600']
                  ].map(([l, v, cls]) => (
                    <div key={l} className={`${cls} rounded-[1.25rem] p-4 text-center border-2 shadow-sm`}>
                      <p className="text-2xl font-black">{v}</p>
                      <p className="text-[10px] font-black uppercase tracking-widest mt-1 opacity-60">{l}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-bold text-red-700">{result.message}</p>
              )}

              {result.failed_details && result.failed_details.length > 0 && (
                <div className="mt-6 space-y-3">
                  <p className="text-[10px] font-black uppercase text-red-600 tracking-widest px-1">Issues Found</p>
                  <div className="bg-white rounded-2xl border border-red-100 overflow-hidden max-h-48 overflow-y-auto custom-scrollbar">
                    {result.failed_details.map((fail, i) => (
                      <div key={i} className="px-4 py-3 border-b border-red-50 last:border-0 flex justify-between items-center hover:bg-red-50/50 transition-colors">
                        <div className="flex items-center space-x-3">
                          <span className="text-[10px] font-black text-red-400 bg-red-50 px-2 py-0.5 rounded uppercase font-mono">Row {fail.row || (i + 1)}</span>
                          <span className="text-xs font-bold text-surface-700">{fail.mobile || 'Unknown'}</span>
                        </div>
                        <span className="text-[11px] font-medium text-red-600 italic">{fail.reason || 'Data Mismatch'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Required Headers Section */}
      <div className="flex flex-col bg-surface-900 rounded-[2.5rem] p-8 text-white shadow-xl shadow-surface-200/50 overflow-hidden relative">
        <div className="absolute top-0 right-0 p-8 opacity-10">
          <SafeIcon icon={FiTerminal} className="w-24 h-24" />
        </div>
        
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/20">
            <SafeIcon icon={FiInfo} className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-sm font-black uppercase tracking-[0.2em]">CSV Structure Requirements</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            'language',
            'mobile_no',
            'outlet_name',
            'achieved_slab',
            'gift_option_1',
            'gift_option_2',
            'gift_option_3',
            'pan_card'
          ].map((header) => (
            <div key={header} className="flex items-center space-x-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
              <code className="text-[10px] font-mono text-primary-300 truncate">{header}</code>
            </div>
          ))}
        </div>

        <div className="space-y-4 border-t border-white/10 pt-6">
          <div className="flex items-start space-x-3">
            <div className="mt-1 flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            </div>
            <p className="text-[11px] text-surface-300 leading-relaxed">
              <span className="text-amber-400 font-black uppercase">PAN Requirement:</span> If a Pan Number is required for the user, add <span className="text-white font-bold italic underline">'Pending'</span> in the <code className="text-primary-300">pan_card</code> column.
            </p>
          </div>
          
          <div className="flex items-start space-x-3">
            <div className="mt-1 flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            </div>
            <p className="text-[11px] text-surface-300 leading-relaxed">
              <span className="text-emerald-400 font-black uppercase">Language:</span> Values should be lowercase (e.g., english, hindi, marathi).
            </p>
          </div>

          <div className="flex items-start space-x-3">
            <div className="mt-1 flex-shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            </div>
            <p className="text-[11px] text-surface-300 leading-relaxed uppercase font-bold italic opacity-60">
              * Note: Column order must be maintained and header names must be exact.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadUsers;